import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, Col, Row, Spinner, Form, Button } from "react-bootstrap";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiRefreshCw,
  FiPause,
  FiPlay,
  FiFileText,
  FiDownload,
} from "react-icons/fi";
import Header from "../components/Header";
import { getHost } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const TIME_RANGES = [
  { label: "15m", hours: 0.25 },
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
  { label: "72h", hours: 72 },
];

const SEVERITIES = [
  { key: "all", label: "All" },
  { key: "success", label: "Success (blue)" },
  { key: "warn", label: "Warnings" },
  { key: "error", label: "Errors" },
];

const REFRESH_MS = 15000;

function levelColor(level) {
  if (level === "error") return "#FF4444";
  if (level === "warn") return "#FFC107";
  if (level === "success") return "#00E9E9"; // loguru LIGHTCYAN
  return "#B0B0B0"; // info — muted grey
}

function LogRow({ log }) {
  const dt = new Date(log.ts);
  const ts = `${String(dt.getHours()).padStart(2, "0")}:${String(
    dt.getMinutes()
  ).padStart(2, "0")}:${String(dt.getSeconds()).padStart(2, "0")}`;
  return (
    <div
      className="log-row d-flex align-items-start gap-2 px-3 py-1"
      style={{
        fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: 12,
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      <span className="text-white-45" style={{ minWidth: 70 }}>
        {ts}
      </span>
      <span
        style={{
          color: levelColor(log.level),
          fontWeight: 600,
          fontSize: 10,
          textTransform: "uppercase",
          minWidth: 46,
        }}
      >
        {log.level}
      </span>
      {log.memory_mb !== null && (
        <span className="text-white-65" style={{ minWidth: 56 }}>
          {log.memory_mb} MB
        </span>
      )}
      <span className="text-white-45" style={{ minWidth: 70 }}>
        [{log.pod?.slice(-6) || "?"}]
      </span>
      <span className="log-row-message text-main" style={{ flex: 1 }}>
        {log.message || "(unparsed)"}
      </span>
    </div>
  );
}

const MonitorLogs = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { toggleSidebar } = useOutletContext() || {};

  const [hours, setHours] = useState(1);
  const [severity, setSeverity] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [monitorName, setMonitorName] = useState(code || "");
  const [monitors, setMonitors] = useState([]);
  const [monitorSearch, setMonitorSearch] = useState("");
  const [showMonitorDropdown, setShowMonitorDropdown] = useState(false);
  const monitorDropdownRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    function onClickOutside(e) {
      if (monitorDropdownRef.current && !monitorDropdownRef.current.contains(e.target)) {
        setShowMonitorDropdown(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    fetch(`${getHost()}/api/monitors`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const byFolder = new Map();
        (d.monitors || [])
          .filter((m) => m.folder_name)
          .forEach((m) => {
            const prev = byFolder.get(m.folder_name);
            if (!prev || (prev.runtime_type !== "k8s" && m.runtime_type === "k8s")) {
              byFolder.set(m.folder_name, m);
            }
          });
        const list = [...byFolder.values()]
          .map((m) => ({ folder_name: m.folder_name, name: m.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setMonitors(list);
      })
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback(
    async (silent = false) => {
      if (!code) return;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          hours: String(hours),
          severity,
          limit: "1000",
        });
        if (searchDebounced) params.set("search", searchDebounced);

        const res = await fetch(
          `${getHost()}/api/admin/observability/${encodeURIComponent(
            code
          )}/logs?${params.toString()}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setLogs(data.logs || []);
        setMonitorName(data.monitor?.name || code);
      } catch (err) {
        setError(err.message);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [code, hours, severity, searchDebounced]
  );

  useEffect(() => {
    if (code) fetchLogs();
  }, [fetchLogs, code]);

  useEffect(() => {
    if (!autoRefresh || !code) return;
    const id = setInterval(() => fetchLogs(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs, code]);

  const download = () => {
    if (!logs.length) return;
    const chronological = [...logs].sort((a, b) => (a.ts < b.ts ? -1 : 1));
    const text = chronological
      .map(
        (l) =>
          `${l.ts}  ${(l.level || "info").padEnd(5)}  ${
            l.memory_mb !== null ? String(l.memory_mb).padStart(5) + " MB" : "        "
          }  [${l.pod?.slice(-6) || "?"}]  ${l.message || ""}`
      )
      .join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `${code}_${stamp}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />

      <div className="pb-3 mx-4 content-body">
        <div className="d-flex align-items-center mb-2">
          <Button
            variant="link"
            className="p-0 me-2 text-white-65"
            onClick={() => navigate(-1)}
            title="Back"
          >
            <FiArrowLeft />
          </Button>
          <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 me-2">
            <FiFileText className="text-white" />
          </div>
          <div>
            <h4 className="fs-22 text-main mb-0">
              Logs {monitorName && <>— {monitorName}</>}
            </h4>
            <p className="text-white-65 fs-13 mb-0">{code || "select a monitor"}</p>
          </div>
          {code && (
            <Button
              variant={autoRefresh ? "outline-success" : "outline-secondary"}
              size="sm"
              className="ms-auto me-2"
              onClick={() => setAutoRefresh(!autoRefresh)}
              title="Auto-refresh every 15s"
            >
              {autoRefresh ? <FiPause /> : <FiPlay />}
            </Button>
          )}
          {code && (
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={download}
              disabled={!logs.length}
              title="Download visible logs as .log"
            >
              <FiDownload />
            </Button>
          )}
          {code && (
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => fetchLogs()}
              disabled={loading}
              title="Refresh"
            >
              <FiRefreshCw />
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 mb-3">
        <Card className="border border-dark bg-transparent">
          <Card.Body className="p-3">
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label className="fs-12 text-white-65 mb-1">Monitor</Form.Label>
                <div ref={monitorDropdownRef} style={{ position: "relative" }}>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={
                      code
                        ? monitors.find((m) => m.folder_name === code)?.name ||
                          code
                        : "Search monitors…"
                    }
                    value={monitorSearch}
                    onChange={(e) => {
                      setMonitorSearch(e.target.value);
                      setShowMonitorDropdown(true);
                    }}
                    onFocus={() => setShowMonitorDropdown(true)}
                    className="form-input-field rounded-3 text-main"
                  />
                  {showMonitorDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        maxHeight: 300,
                        overflowY: "auto",
                        zIndex: 1000,
                        background: "#1a1a2e",
                        border: "1px solid #333",
                        borderRadius: 8,
                        marginTop: 4,
                      }}
                    >
                      {monitors
                        .filter(
                          (m) =>
                            m.name
                              .toLowerCase()
                              .includes(monitorSearch.toLowerCase()) ||
                            m.folder_name
                              .toLowerCase()
                              .includes(monitorSearch.toLowerCase())
                        )
                        .slice(0, 100)
                        .map((m) => (
                          <div
                            key={m.folder_name}
                            onClick={() => {
                              setShowMonitorDropdown(false);
                              setMonitorSearch("");
                              navigate(
                                `/admin/logs/${encodeURIComponent(m.folder_name)}`
                              );
                            }}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid #222",
                              color: code === m.folder_name ? "#fff" : "#aaa",
                              background:
                                code === m.folder_name
                                  ? "#2a2a4a"
                                  : "transparent",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#2a2a4a")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background =
                                code === m.folder_name
                                  ? "#2a2a4a"
                                  : "transparent")
                            }
                          >
                            {m.name}
                            <span
                              style={{
                                float: "right",
                                color: "#666",
                                fontSize: 12,
                              }}
                            >
                              {m.folder_name}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">Time range</Form.Label>
                <div className="d-flex flex-wrap gap-1">
                  {TIME_RANGES.map((r) => (
                    <Button
                      key={r.label}
                      size="sm"
                      variant={hours === r.hours ? "primary" : "outline-secondary"}
                      onClick={() => setHours(r.hours)}
                    >
                      {r.label}
                    </Button>
                  ))}
                </div>
              </Col>
              <Col md={2}>
                <Form.Label className="fs-12 text-white-65 mb-1">Severity</Form.Label>
                <Form.Select
                  size="sm"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  {SEVERITIES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">Search (regex)</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="e.g. 403|traceback"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      <div className="px-4 mb-3">
        <Card className="border border-dark bg-transparent">
          <Card.Body className="logs-scroll p-0" style={{ maxHeight: "68vh", overflowY: "auto" }}>
            {!code && (
              <div className="p-3 text-white-65 fs-13">
                Pick a monitor from the dropdown above to view its logs.
              </div>
            )}
            {code && loading && (
              <div className="text-center py-4 text-white-65">
                <Spinner animation="border" size="sm" /> loading…
              </div>
            )}
            {error && (
              <div className="p-3" style={{ color: "#FF4444" }}>
                Error: {error}
              </div>
            )}
            {code && !loading && !error && logs.length === 0 && (
              <div className="p-3 text-white-65 fs-13">
                No log lines in this range. Try widening the time range or
                clearing filters.
              </div>
            )}
            {logs.map((log, i) => (
              <LogRow key={i} log={log} />
            ))}
            {code && logs.length > 0 && (
              <div className="p-2 text-white-45 fs-12 text-center">
                {logs.length} lines
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default MonitorLogs;
